import { getChatSessionsQueryKey, postChatSessionsMutation } from '@onefootprint/fetch-dashboard';
import { useRequestErrorToast } from '@onefootprint/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type ResourceContext, setInitialPrompt } from '@/components/agent-chat';
import { stashChatSession } from '@/components/agent-chat/utils/chat-session-stash';
export type StartChatArgs = {
  query: string;
  files: File[];
  env?: string;
  /**
   * The env the chat should be created in. Sent as a per-request `x-is-live`
   * header so the backend stamps the session correctly — without touching the
   * global session env (which would trigger an app-wide `resetQueries`).
   */
  isLive: boolean;
  /** Resource the chat is about; carried to the first WS message via the initial-prompt stash. */
  context?: ResourceContext;
  /** Resources @-mentioned in the first message; carried to the first WS message and swapped into the title copy to show their names. */
  mentions?: ResourceContext[];
  onSuccess?: (sessionId: string) => void;
};

// Only the title copy gets names swapped in: the transcript copy keeps the raw ids so mention pills
// can be reconstructed from them. Entities are skipped — their names are sensitive, so the id is the
// intended fallback there.
const withMentionNamesForTitle = (text: string, mentions?: ResourceContext[]): string => {
  if (!mentions?.length) return text;
  let out = text;
  for (const m of mentions) {
    if ((m.kind === 'agent' || m.kind === 'playbook') && m.name) out = out.split(m.id).join(m.name);
  }
  return out;
};
const useStartChat = () => {
  const showErrorToast = useRequestErrorToast();
  const queryClient = useQueryClient();
  const createSession = useMutation(postChatSessionsMutation());
  const submit = async ({
    query,
    files,
    env,
    isLive,
    context,
    mentions,
    onSuccess
  }: StartChatArgs) => {
    try {
      const response = await createSession.mutateAsync({
        body: {
          model: 'default',
          initialPrompt: withMentionNamesForTitle(query, mentions),
          env
        },
        headers: {
          'x-is-live': String(isLive)
        }
      });
      const {
        sessionId
      } = response;
      stashChatSession(sessionId, response, isLive);
      setInitialPrompt({
        chatSessionId: sessionId,
        files,
        prompt: query,
        context,
        mentions
      });
      // Refresh the sidebar list so the new chat shows under the right env
      // section; the active chat itself renders from the stash meanwhile.
      queryClient.invalidateQueries({
        queryKey: getChatSessionsQueryKey()
      });
      onSuccess?.(sessionId);
    } catch (error) {
      showErrorToast(error);
      throw error;
    }
  };
  return {
    submit,
    isLoading: createSession.isPending,
    isError: createSession.isError,
    reset: createSession.reset
  };
};
export default useStartChat;